import dayjs from "@eisox/dayjs";

import type { Locale } from "../../Locale";

type SegmentType = "year" | "month" | "day" | "hour24" | "hour12" | "minute" | "second" | "meridiem";
type NumericSegmentType = Exclude<SegmentType, "meridiem">;

interface SegmentBase {
  readonly kind: "segment";
  /** Position among the editable segments, in reading order. */
  readonly index: number;
  /** Padded dayjs token — renders the value and rebuilds the pattern. */
  readonly token: string;
  readonly length: number;
}

interface NumericSegment extends SegmentBase {
  readonly type: NumericSegmentType;
  readonly min: number;
  readonly max: number;
}

interface MeridiemSegment extends SegmentBase {
  readonly type: "meridiem";
}

type Segment = NumericSegment | MeridiemSegment;

interface Literal {
  readonly kind: "literal";
  readonly text: string;
}

type FormatPart = Segment | Literal;

interface ParsedFormat {
  readonly locale: Locale;
  readonly parts: readonly FormatPart[];
  readonly segments: readonly Segment[];
  /** A type appears at most once, so its position is enough to address it. */
  readonly indexByType: ReadonlyMap<SegmentType, number>;
  /** Canonical dayjs pattern rebuilt from the parts — parses exactly what the field displays. */
  readonly pattern: string;
}

const NUMERIC_SEGMENTS = new Map<string, { type: NumericSegmentType; min: number; max: number }>([
  ["YYYY", { type: "year", min: 1, max: 9999 }],
  ["YY", { type: "year", min: 0, max: 99 }],
  ["MM", { type: "month", min: 1, max: 12 }],
  ["DD", { type: "day", min: 1, max: 31 }],
  ["HH", { type: "hour24", min: 0, max: 23 }],
  ["hh", { type: "hour12", min: 1, max: 12 }],
  ["mm", { type: "minute", min: 0, max: 59 }],
  ["ss", { type: "second", min: 0, max: 59 }],
]);

const MERIDIEM_TOKEN = "A";
const MERIDIEM_LENGTH = 2;

const PADDED_TOKEN = new Map<string, string>([
  ["M", "MM"],
  ["D", "DD"],
  ["H", "HH"],
  ["h", "hh"],
  ["m", "mm"],
  ["s", "ss"],
  ["a", MERIDIEM_TOKEN],
]);

const EXPANDABLE_TOKENS = new Set(["L", "LT", "LTS"]);
const MONTH_NAME_TOKENS = new Set(["l", "ll", "lll", "llll", "LL", "LLL", "LLLL"]);

const EDITABLE_TOKENS = [...NUMERIC_SEGMENTS.keys(), MERIDIEM_TOKEN, ...PADDED_TOKEN.keys()].join(", ");

/** An escaped literal, or a maximal run of letters — a run must spell exactly one token. */
const PART_PATTERN = /\[([^\]]*)\]|([A-Za-z]+)/g;

const longDateFormat = (token: string, locale: Locale): string => {
  // Typed `string`, but dayjs hands back undefined for a format the locale never defined.
  const expansion = dayjs().locale(locale).localeData().longDateFormat(token);
  if (!expansion) throw new Error(`DateField: locale "${locale}" does not define the "${token}" format.`);
  return expansion;
};

interface Lexer {
  readonly locale: Locale;
  readonly source: string;
  readonly parts: FormatPart[];
  readonly segments: Segment[];
}

const appendLiteral = (lexer: Lexer, text: string): void => {
  if (text === "") return;

  const last = lexer.parts.at(-1);
  if (last?.kind === "literal") lexer.parts[lexer.parts.length - 1] = { kind: "literal", text: last.text + text };
  else lexer.parts.push({ kind: "literal", text });
};

const appendSegment = (lexer: Lexer, build: (index: number) => Segment): void => {
  const segment = build(lexer.segments.length);
  lexer.parts.push(segment);
  lexer.segments.push(segment);
};

const appendRun = (lexer: Lexer, run: string, expandable: boolean): void => {
  if (MONTH_NAME_TOKENS.has(run))
    throw new Error(
      `DateField: localized token "${run}" is not supported in format "${lexer.source}" — it renders a month or day name, which cannot be edited segment by segment. Use L, LT, LTS, or an explicit numeric format (${EDITABLE_TOKENS}).`,
    );

  if (EXPANDABLE_TOKENS.has(run)) {
    if (!expandable)
      throw new Error(`DateField: locale "${lexer.locale}" expands "${lexer.source}" to another localized token.`);
    appendFormat(lexer, longDateFormat(run, lexer.locale), false);
    return;
  }

  const token = PADDED_TOKEN.get(run) ?? run;
  if (token === MERIDIEM_TOKEN) {
    appendSegment(lexer, index => ({ kind: "segment", index, type: "meridiem", token, length: MERIDIEM_LENGTH }));
    return;
  }

  const numeric = NUMERIC_SEGMENTS.get(token);
  if (!numeric)
    throw new Error(
      `DateField: unsupported token "${run}" in format "${lexer.source}". Editable tokens are ${EDITABLE_TOKENS}; wrap free text in [brackets].`,
    );

  appendSegment(lexer, index => ({ kind: "segment", index, token, length: token.length, ...numeric }));
};

const appendFormat = (lexer: Lexer, format: string, expandable: boolean): void => {
  let cursor = 0;

  for (const match of format.matchAll(PART_PATTERN)) {
    const [, escaped, run] = match;
    appendLiteral(lexer, format.slice(cursor, match.index));

    if (run !== undefined) {
      cursor = match.index + run.length;
      appendRun(lexer, run, expandable);
      continue;
    }

    const literal = escaped ?? "";
    if (literal === "")
      throw new Error(`DateField: empty "[]" literal in format "${lexer.source}" — dayjs cannot parse it.`);
    if (literal.includes("["))
      throw new Error(`DateField: unbalanced "[" inside the literal "[${literal}]" of format "${lexer.source}".`);

    cursor = match.index + literal.length + 2;
    appendLiteral(lexer, literal);
  }

  appendLiteral(lexer, format.slice(cursor));
};

const indexSegments = (lexer: Lexer): ReadonlyMap<SegmentType, number> => {
  const indexByType = new Map<SegmentType, number>();

  for (const segment of lexer.segments) {
    if (indexByType.has(segment.type))
      throw new Error(
        `DateField: format "${lexer.source}" repeats the ${segment.type} segment. A field edits one value — use mode="range" for two bounds.`,
      );
    indexByType.set(segment.type, segment.index);
  }

  if (indexByType.size === 0)
    throw new Error(`DateField: format "${lexer.source}" has no editable segment — the field could never be valid.`);
  if (indexByType.has("meridiem") && !indexByType.has("hour12"))
    throw new Error(`DateField: format "${lexer.source}" has a meridiem segment but no 12-hour token (hh).`);

  return indexByType;
};

const toPattern = (parts: readonly FormatPart[]): string =>
  parts.map(part => (part.kind === "literal" ? `[${part.text}]` : part.token)).join("");

const buildFormat = (format: string, locale: Locale): ParsedFormat => {
  const lexer: Lexer = { locale, source: format, parts: [], segments: [] };
  appendFormat(lexer, format, true);

  return {
    locale,
    parts: lexer.parts,
    segments: lexer.segments,
    indexByType: indexSegments(lexer),
    pattern: toPattern(lexer.parts),
  };
};

// Formats are static literals and dayjs locales load once, so a parse never goes stale.
// Failures stay out of the cache: an invalid format must throw with a fresh stack every time.
const CACHE = new Map<string, ParsedFormat>();

const parseFormat = (format: string, locale: Locale): ParsedFormat => {
  const key = `${locale}|${format}`;
  const cached = CACHE.get(key);
  if (cached) return cached;

  const parsed = buildFormat(format, locale);
  CACHE.set(key, parsed);
  return parsed;
};

export { parseFormat };
export type {
  FormatPart,
  Literal,
  MeridiemSegment,
  NumericSegment,
  NumericSegmentType,
  ParsedFormat,
  Segment,
  SegmentType,
};
